import React, { useState, useEffect, useMemo } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  TextField,
  InputAdornment,
  Tooltip,
  LinearProgress,
  TableSortLabel,
  Badge
} from '@mui/material';
import {
  CheckCircle,
  Receipt,
  AttachMoney,
  BarChart,
  Search,
  FilterList,
  TrendingUp,
  Download,
  Cancel,
  Pending,
  Close
} from '@mui/icons-material';
import Chart from 'react-apexcharts';
import { GetShipmentClaims } from '../../apis/Agency';
import * as XLSX from 'xlsx';

const ClaimsDashboard = ({ shipments }) => {
  // State
  const [claims, setClaims] = useState([]);
  const [claimsLoader, setClaimsLoader] = useState(true);
  const [filters, setFilters] = useState({
    status: 'all',
    carrier: 'all',
    type: 'all',
    search: ''
  });
  const [page, setPage] = useState(1);
  const ROWS_PER_PAGE = 10;

  // Fetch data
  useEffect(() => {
    const fetchClaims = async () => {
      try {
        const res = await GetShipmentClaims();
        if (res?.status === 200) {
          setClaims(res.data.data);
          setClaimsLoader(false);
        }
      } catch (error) {
        console.error('Failed to fetch claims:', error);
        setClaimsLoader(false);
      }
    };
    fetchClaims();
  }, []);

  // Memoized calculations
  const {
    kpis,
    carrierTrends,
    claimTypes,
    statusData,
    routeSummaries,
    carriers
  } = useMemo(() => {
    if (!claims.length) return {};

    // Calculate KPIs
    const totalClaims = claims.length;
    const totalShipments = shipments.length;

    const statusCounts = {
      Paid: claims.filter(c => c.claimStatus === 'Paid').length,
      Submitted: claims.filter(c => c.claimStatus === 'Submitted').length,
      Rejected: claims.filter(c => c.claimStatus === 'Rejected').length
    };

    const totalValue = claims.reduce((sum, c) => sum + (c.claimAmount || 0), 0);
    const paidValue = claims
      .filter(c => c.claimStatus === 'Paid')
      .reduce((sum, c) => sum + (c.claimAmountReceived || 0), 0);
    const avgValue = totalClaims ? totalValue / totalClaims : 0;
    const claimRate = totalShipments ? (totalClaims / totalShipments) * 100 : 0;

    // Carrier trends for chart
    const carrierMap = {};
    const carrierList = new Set();

    claims.forEach(claim => {
      // const shipment = shipments.find(s => s.id === claim.shipmentId);
      // if (shipment?.carrierName) {
      const name = claim.carrierName;
      carrierList.add(name);
      if (!carrierMap[name]) {
        carrierMap[name] = {
          name,
          count: 0,
          value: 0,
          paid: 0,
          rejected: 0,
          pending: 0
        };
      }
      carrierMap[name].count++;
      carrierMap[name].value += claim.claimAmount || 0;

      if (claim.claimStatus === 'Paid') carrierMap[name].paid++;
      if (claim.claimStatus === 'Rejected') carrierMap[name].rejected++;
      if (claim.claimStatus === 'Submitted') carrierMap[name].pending++;
      // }
    });

    // Claim types
    const typeMap = {};
    claims.forEach(claim => {
      const type = claim.claimType || 'Other';
      if (!typeMap[type]) {
        typeMap[type] = { name: type, count: 0, value: 0 };
      }
      typeMap[type].count++;
      typeMap[type].value += claim.claimAmount || 0;
    });

    // Route summaries for chart
    const routeMap = {};
    claims.forEach(claim => {
      const shipment = shipments.find(s => s.id === claim.shipmentId);
      if (shipment?.originCity && shipment?.destinationCity) {
        const routeKey = `${shipment.originCity}-${shipment.destinationCity}`;
        if (!routeMap[routeKey]) {
          routeMap[routeKey] = {
            route: `${shipment.originCity} → ${shipment.destinationCity}`,
            routeShort: `${shipment.originCity.substring(
              0,
              3
            )}-${shipment.destinationCity.substring(0, 3)}`,
            count: 0,
            value: 0,
            carriers: new Set()
          };
        }
        routeMap[routeKey].count++;
        routeMap[routeKey].value += claim.claimAmount || 0;
        if (shipment.carrierName) {
          routeMap[routeKey].carriers.add(shipment.carrierName);
        }
      }
    });

    return {
      kpis: {
        totalClaims,
        totalShipments,
        claimsAccepted: statusCounts.Paid,
        claimRate,
        totalValue,
        paidValue,
        avgValue,
        ...statusCounts
      },
      carrierTrends: Object.values(carrierMap)
        .map(c => ({
          ...c,
          rejectionRate: (c.rejected / c.count) * 100
        }))
        .sort((a, b) => b.count - a.count),
      claimTypes: Object.values(typeMap).map(t => ({
        ...t,
        percentage: (t.count / totalClaims) * 100
      })),
      statusData: [
        statusCounts.Paid,
        statusCounts.Submitted,
        statusCounts.Rejected
      ],
      routeSummaries: Object.values(routeMap)
        .map(r => ({
          ...r,
          carrierCount: r.carriers.size,
          avgValue: r.value / r.count
        }))
        .sort((a, b) => b.count - a.count),
      carriers: Array.from(carrierList)
    };
  }, [claims, shipments]);

  // Filtered claims
  const filteredClaims = useMemo(() => {
    return claims.filter(claim => {
      if (filters.status !== 'all' && claim.claimStatus !== filters.status)
        return false;
      if (filters.type !== 'all' && claim.claimType !== filters.type)
        return false;

      if (filters.carrier !== 'all') {
        const shipment = shipments.find(s => s.id === claim.shipmentId);
        if (shipment?.carrierName !== filters.carrier) return false;
      }

      if (filters.search) {
        const term = filters.search.toLowerCase();
        const fields = [
          claim.claimNumber,
          claim.carrierCheckNumber,
          claim.affectedPONumber,
          claim.claimType,
          claim.claimStatus
        ];
        if (!fields.some(f => f?.toLowerCase().includes(term))) return false;
      }

      return true;
    });
  }, [claims, filters, shipments]);

  // Get unique claim types for filter dropdown
  const claimTypesList = useMemo(() => {
    const types = new Set();
    claims.forEach(claim => {
      if (claim.claimType) types.add(claim.claimType);
    });
    return Array.from(types);
  }, [claims]);

  // Active filters count
  const activeFiltersCount = useMemo(() => {
    return Object.values(filters).filter(
      value => value !== 'all' && value !== ''
    ).length;
  }, [filters]);

  // Pagination

  // Handlers
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilter = key => {
    const defaultValue = key === 'search' ? '' : 'all';
    handleFilterChange(key, defaultValue);
  };

  const clearAllFilters = () => {
    setFilters({
      status: 'all',
      carrier: 'all',
      type: 'all',
      search: ''
    });
    setPage(1);
  };

  // Chart options
  const statusChartOptions = {
    chart: {
      type: 'donut',
      toolbar: { show: false }
    },
    labels: ['Paid', 'Pending', 'Rejected'],
    colors: ['#4caf50', '#ff9800', '#f44336'],
    legend: {
      position: 'bottom',
      fontSize: '12px'
    },
    plotOptions: {
      pie: {
        donut: {
          size: '60%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total Claims',
              fontSize: '14px',
              formatter: () => kpis?.totalClaims || 0
            }
          }
        }
      }
    },
    dataLabels: {
      enabled: false
    },
    tooltip: {
      y: {
        formatter: val => val,
        title: {
          formatter: () => 'Claims'
        }
      }
    }
  };

  const typeChartOptions = {
    chart: {
      type: 'pie',
      toolbar: { show: false }
    },
    labels: claimTypes?.map(t => t.name) || [],
    colors:
      claimTypes?.map(
        (_, i) => `hsl(${i * (360 / Math.max(claimTypes.length, 1))}, 70%, 50%)`
      ) || [],
    legend: {
      position: 'right',
      fontSize: '12px'
    },
    dataLabels: {
      enabled: true,
      formatter: val => `${val.toFixed(1)}%`
    },
    tooltip: {
      y: {
        formatter: val => val,
        title: {
          formatter: seriesName => `${seriesName} Claims`
        }
      }
    }
  };

  // NEW: Carrier Chart Options
  const carrierChartOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false }
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: 20,
        columnWidth: '8%',
        borderRadius: 4,
        dataLabels: {
          position: 'center'
        }
      }
    },
    colors: ['#2196f3'],
    dataLabels: {
      enabled: true,
      textAnchor: 'start',
      style: {
        colors: ['#fff']
      },
      formatter: val => val,
      offsetX: 10
    },
    xaxis: {
      categories: carrierTrends?.slice(0, 5).map(c => c.name) || [],
      labels: {
        style: {
          fontSize: '11px'
        }
      }
    },
    yaxis: {
      labels: {
        style: {
          fontSize: '11px'
        }
      }
    },
    tooltip: {
      y: {
        formatter: val => `${val} claims`,
        title: {
          formatter: () => ''
        }
      }
    }
  };

  // NEW: Route Chart Options
  const routeChartOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false }
    },
    colors: ['#9c27b0'],
    plotOptions: {
      bar: {
        borderRadius: 4,
        columnWidth: '60%'
      }
    },
    dataLabels: {
      enabled: false
    },
    xaxis: {
      categories: routeSummaries?.slice(0, 5).map(r => r.routeShort) || [],
      labels: {
        style: {
          fontSize: '11px'
        }
      }
    },
    yaxis: {
      title: {
        text: 'Claims Count'
      }
    },
    tooltip: {
      y: {
        formatter: val => `${val} claims`,
        title: {
          formatter: (seriesName, opts) =>
            routeSummaries?.[opts.dataPointIndex]?.route || 'Route'
        }
      }
    }
  };

  // Sorting state
  const [sort, setSort] = useState({
    field: 'filedDate',
    direction: 'desc'
  });

  // Date formatter
  const formatDate = dateString => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Sorting function
  const sortedClaims = useMemo(() => {
    const sorted = [...filteredClaims];

    sorted.sort((a, b) => {
      let aValue, bValue;

      switch (sort.field) {
        case 'claimAmount':
          aValue = a.claimAmount || 0;
          bValue = b.claimAmount || 0;
          break;
        case 'filedDate':
          aValue = new Date(a.filedDate || 0).getTime();
          bValue = new Date(b.filedDate || 0).getTime();
          break;
        case 'carrierName':
          const shipmentA = shipments.find(s => s.id === a.shipmentId);
          const shipmentB = shipments.find(s => s.id === b.shipmentId);
          aValue = shipmentA?.carrierName || '';
          bValue = shipmentB?.carrierName || '';
          break;
        case 'claimNumber':
          aValue = a.claimNumber || '';
          bValue = b.claimNumber || '';
          break;
        case 'claimStatus':
          aValue = a.claimStatus || '';
          bValue = b.claimStatus || '';
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sort.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sort.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredClaims, sort, shipments]);

  // Handle sort click
  const handleSort = field => {
    setSort(prev => ({
      field,
      direction:
        prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setPage(1);
  };

  // Export to Excel function
  const handleExportExcel = () => {
    const exportData = filteredClaims.map(claim => {
      const shipment = shipments.find(s => s.id === claim.shipmentId);
      return {
        'Claim #': claim.claimNumber || '',
        Status: claim.claimStatus || '',
        Type: claim.claimType || '',
        Carrier: shipment?.carrierName || '',
        Amount: claim.claimAmount || 0,
        'Amount Received': claim.claimAmountReceived || 0,
        'Filed Date': claim.filedDate ? formatDate(claim.filedDate) : '',
        'PO Number': claim.affectedPONumber || '',
        'Carrier Check #': claim.carrierCheckNumber || '',
        Description: claim.claimDescription || ''
      };
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Claims');

    // Auto-size columns
    const wscols = [
      { wch: 12 },
      { wch: 10 },
      { wch: 15 },
      { wch: 20 },
      { wch: 12 },
      { wch: 15 },
      { wch: 12 },
      { wch: 15 },
      { wch: 15 },
      { wch: 30 }
    ];
    ws['!cols'] = wscols;

    XLSX.writeFile(
      wb,
      `Claims_Report_${new Date().toISOString().split('T')[0]}.xlsx`
    );
  };

  // Update paginated claims to use sorted data
  const sortedAndPaginatedClaims = useMemo(() => {
    const start = (page - 1) * ROWS_PER_PAGE;
    return sortedClaims.slice(start, start + ROWS_PER_PAGE);
  }, [sortedClaims, page]);

  // Components
  const KpiCard = ({ title, value, icon, color = 'primary', subtitle }) => (
    <Card
      sx={{
        height: '100%',
        borderRadius: 2,
        boxShadow: 2,
        borderLeft: `4px solid`,
        borderColor: `${color}.main`,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4
        }
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='flex-start'
        >
          <Box>
            <Typography
              variant='caption'
              color='text.secondary'
              fontWeight={500}
            >
              {title}
            </Typography>
            <Typography variant='h5' fontWeight={700} color={`${color}.main`}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant='caption' color='text.secondary'>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              color: `${color}.main`,
              opacity: 0.8,
              backgroundColor: `${color}.light`,
              borderRadius: '50%',
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const StatusBadge = ({ status }) => {
    const config = {
      Paid: { color: 'success', icon: <CheckCircle fontSize='small' /> },
      Submitted: { color: 'warning', icon: <Pending fontSize='small' /> },
      Rejected: { color: 'error', icon: <Cancel fontSize='small' /> }
    }[status] || { color: 'default', icon: null };

    return (
      <Chip
        icon={config.icon}
        label={status}
        color={config.color}
        size='small'
        variant='outlined'
        sx={{ fontWeight: 500 }}
      />
    );
  };

  if (claimsLoader) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        height='60vh'
      >
        <LinearProgress sx={{ width: '60%' }} />
      </Box>
    );
  }
  if (!claims.length && !claimsLoader) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        height='60vh'
      >
        No Claims Data Available!
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* KPI Cards - Enhanced Kag-style */}
      <Grid container spacing={2} mb={4}>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard
            title='Total Claims'
            value={kpis?.totalClaims || 0}
            icon={<Receipt />}
            color='primary'
            subtitle={`${kpis?.totalShipments || 0} shipments`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard
            title='Claims Accepted'
            value={kpis?.claimsAccepted || 0}
            icon={<CheckCircle />}
            color='success'
            subtitle={`${
              kpis?.claimsAccepted > 0
                ? ((kpis.claimsAccepted / kpis.totalClaims) * 100).toFixed(1)
                : 0
            }% acceptance rate`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard
            title='Claims Rate'
            value={`${(kpis?.claimRate || 0).toFixed(1)}%`}
            icon={<BarChart />}
            color='info'
            subtitle={`of total shipments`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard
            title='Total Claim Value'
            value={`$${(kpis?.totalValue || 0).toLocaleString()}`}
            icon={<AttachMoney />}
            color='warning'
            subtitle={`Paid: $${(kpis?.paidValue || 0).toLocaleString()}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard
            title='Average Claim'
            value={`$${(kpis?.avgValue || 0).toLocaleString(undefined, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            })}`}
            icon={<TrendingUp />}
            color='secondary'
            subtitle={`per claim`}
          />
        </Grid>
      </Grid>

      {/* Active Filters Display */}
      {activeFiltersCount > 0 && (
        <Paper sx={{ p: 1.5, borderRadius: 2, mb: 3, bgcolor: 'action.hover' }}>
          <Box
            display='flex'
            alignItems='center'
            justifyContent='space-between'
          >
            <Box display='flex' alignItems='center' gap={1}>
              <FilterList fontSize='small' color='primary' />
              <Typography variant='caption' fontWeight={600}>
                Active Filters ({activeFiltersCount}):
              </Typography>
              {filters.status !== 'all' && (
                <Chip
                  label={`Status: ${filters.status}`}
                  size='small'
                  onDelete={() => clearFilter('status')}
                  deleteIcon={<Close fontSize='small' />}
                />
              )}
              {filters.carrier !== 'all' && (
                <Chip
                  label={`Carrier: ${filters.carrier}`}
                  size='small'
                  onDelete={() => clearFilter('carrier')}
                  deleteIcon={<Close fontSize='small' />}
                />
              )}
              {filters.type !== 'all' && (
                <Chip
                  label={`Type: ${filters.type}`}
                  size='small'
                  onDelete={() => clearFilter('type')}
                  deleteIcon={<Close fontSize='small' />}
                />
              )}
              {filters.search && (
                <Chip
                  label={`Search: ${filters.search}`}
                  size='small'
                  onDelete={() => clearFilter('search')}
                  deleteIcon={<Close fontSize='small' />}
                />
              )}
            </Box>
            <Button
              size='small'
              onClick={clearAllFilters}
              startIcon={<Close fontSize='small' />}
            >
              Clear All
            </Button>
          </Box>
        </Paper>
      )}

      {/* Charts Section - All Required Charts */}
      <Grid container spacing={3} mb={3}>
        {/* Status Distribution */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            <Typography variant='subtitle1' fontWeight={600} mb={2}>
              Status Distribution
            </Typography>
            <Box height={250}>
              <Chart
                options={statusChartOptions}
                series={statusData || []}
                type='donut'
                height='100%'
              />
            </Box>
          </Paper>
        </Grid>

        {/* Claims by Type */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            <Typography variant='subtitle1' fontWeight={600} mb={2}>
              Claims by Type
            </Typography>
            <Box height={250}>
              <Chart
                options={typeChartOptions}
                series={claimTypes?.map(t => t.count) || []}
                type='pie'
                height='100%'
              />
            </Box>
          </Paper>
        </Grid>

        {/* Claims by Carrier - NEW */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            <Typography variant='subtitle1' fontWeight={600} mb={2}>
              Claims by Carrier (Top 5)
            </Typography>
            <Box height={250}>
              <Chart
                options={carrierChartOptions}
                series={[
                  {
                    name: 'Claims',
                    data: carrierTrends?.slice(0, 5).map(c => c.count) || []
                  }
                ]}
                type='bar'
                height='100%'
              />
            </Box>
          </Paper>
        </Grid>

        {/* Claims by Route - NEW */}
        {/* <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            <Typography variant='subtitle1' fontWeight={600} mb={2}>
              Claims by Route (Top 6)
            </Typography>
            <Box height={250}>
              <Chart
                options={routeChartOptions}
                series={[
                  {
                    name: 'Claims',
                    data: routeSummaries?.slice(0, 6).map(r => r.count) || []
                  }
                ]}
                type='bar'
                height='100%'
              />
            </Box>
          </Paper>
        </Grid> */}
      </Grid>

      {/* Claims List View with Filters */}
      <Paper sx={{ borderRadius: 2, mb: 3, boxShadow: 3 }}>
        {/* Enhanced Header with All Filters */}
        <Box p={2} borderBottom='1px solid' borderColor='divider'>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={12} md={4}>
              <Typography variant='h6' fontWeight={600}>
                Claims List
                <Chip
                  label={filteredClaims.length}
                  size='small'
                  color='primary'
                  sx={{ ml: 1 }}
                />
              </Typography>
            </Grid>

            <Grid item xs={12} md={8}>
              <Box
                display='flex'
                gap={1}
                alignItems='center'
                justifyContent='flex-end'
              >
                {/* Search */}
                <TextField
                  size='small'
                  placeholder='Search claims...'
                  value={filters.search}
                  onChange={e => handleFilterChange('search', e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <Search fontSize='small' />
                      </InputAdornment>
                    ),
                    sx: { height: 40 }
                  }}
                  sx={{ width: 200 }}
                />

                {/* Status Filter */}
                <FormControl size='small' sx={{ minWidth: 120 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    onChange={e => handleFilterChange('status', e.target.value)}
                    label='Status'
                  >
                    <MenuItem value='all'>All Status</MenuItem>
                    <MenuItem value='Paid'>Paid</MenuItem>
                    <MenuItem value='Submitted'>Pending</MenuItem>
                    <MenuItem value='Rejected'>Rejected</MenuItem>
                  </Select>
                </FormControl>

                {/* Carrier Filter */}
                <FormControl size='small' sx={{ minWidth: 150 }}>
                  <InputLabel>Carrier</InputLabel>
                  <Select
                    value={filters.carrier}
                    onChange={e =>
                      handleFilterChange('carrier', e.target.value)
                    }
                    label='Carrier'
                  >
                    <MenuItem value='all'>All Carriers</MenuItem>
                    {carriers?.map(carrier => (
                      <MenuItem key={carrier} value={carrier}>
                        {carrier}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Type Filter */}
                <FormControl size='small' sx={{ minWidth: 130 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={filters.type}
                    onChange={e => handleFilterChange('type', e.target.value)}
                    label='Type'
                  >
                    <MenuItem value='all'>All Types</MenuItem>
                    {claimTypesList?.map(type => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Export Button */}
                <Tooltip title='Export to Excel'>
                  <Badge badgeContent={filteredClaims.length} color='primary'>
                    <Button
                      variant='outlined'
                      startIcon={<Download />}
                      onClick={handleExportExcel}
                      size='small'
                    >
                      Export
                    </Button>
                  </Badge>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Table with Sorting */}
        <TableContainer sx={{ maxHeight: 500 }}>
          <Table size='medium' stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sort.field === 'claimNumber'}
                    direction={sort.direction}
                    onClick={() => handleSort('claimNumber')}
                  >
                    Claim #
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sort.field === 'claimStatus'}
                    direction={sort.direction}
                    onClick={() => handleSort('claimStatus')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sort.field === 'carrierName'}
                    direction={sort.direction}
                    onClick={() => handleSort('carrierName')}
                  >
                    Carrier
                  </TableSortLabel>
                </TableCell>
                <TableCell align='right' sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sort.field === 'claimAmount'}
                    direction={sort.direction}
                    onClick={() => handleSort('claimAmount')}
                  >
                    Amount
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel
                    active={sort.field === 'filedDate'}
                    direction={sort.direction}
                    onClick={() => handleSort('filedDate')}
                  >
                    Filed Date
                  </TableSortLabel>
                </TableCell>
                {/* <TableCell sx={{ fontWeight: 600 }}>PO #</TableCell> */}
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedAndPaginatedClaims.map((claim, index) => {
                {
                  /* const shipment = shipments.find(s => s.id === claim.shipmentId); */
                }
                return (
                  <TableRow key={claim.id || index} hover>
                    <TableCell>
                      <Box>
                        <Typography variant='body1' fontWeight={500}>
                          {claim.claimNumber || '—'}
                        </Typography>
                        {claim.carrierCheckNumber && (
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            display='block'
                          >
                            Check: {claim.carrierCheckNumber}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={claim.claimStatus} />
                    </TableCell>
                    <TableCell>
                      <Typography variant='body1' fontWeight={500}>
                        {claim?.carrierName || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align='right'>
                      <Box
                        display='flex'
                        flexDirection='column'
                        alignItems='flex-end'
                      >
                        <Typography
                          variant='body1'
                          fontWeight={600}
                          color='primary.main'
                        >
                          $
                          {(claim.claimAmount || 0).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </Typography>
                        {claim.claimStatus === 'Paid' &&
                          claim.claimAmountReceived > 0 && (
                            <Typography variant='caption' color='success.main'>
                              Received: $
                              {claim.claimAmountReceived.toLocaleString()}
                            </Typography>
                          )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant='body1'>
                        {claim.filedDate ? formatDate(claim.filedDate) : '—'}
                      </Typography>
                    </TableCell>
                    {/* <TableCell>
                      <Chip
                        label={claim.affectedPONumber || '—'}
                        size='small'
                        variant='outlined'
                      />
                    </TableCell> */}
                    <TableCell>
                      <Typography variant='body2' color='text.secondary'>
                        {claim.claimType || '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination Footer */}
        <Box
          p={2}
          display='flex'
          justifyContent='space-between'
          alignItems='center'
        >
          <Typography variant='body2' color='text.secondary'>
            Showing {(page - 1) * ROWS_PER_PAGE + 1} -{' '}
            {Math.min(page * ROWS_PER_PAGE, sortedClaims.length)} of{' '}
            {sortedClaims.length} claims
          </Typography>
          <Pagination
            count={Math.ceil(sortedClaims.length / ROWS_PER_PAGE)}
            page={page}
            onChange={(_, value) => setPage(value)}
            color='primary'
            shape='rounded'
            showFirstButton
            showLastButton
          />
        </Box>
      </Paper>

      {/* Route Summaries Table */}
      {routeSummaries?.length > 0 && (
        <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            mb={2}
          >
            <Typography variant='h6' fontWeight={600}>
              Top Routes by Claims
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Sorted by claim count
            </Typography>
          </Box>
          <TableContainer>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Route</TableCell>
                  <TableCell align='right' sx={{ fontWeight: 600 }}>
                    Claims
                  </TableCell>
                  <TableCell align='right' sx={{ fontWeight: 600 }}>
                    Carriers
                  </TableCell>
                  <TableCell align='right' sx={{ fontWeight: 600 }}>
                    Total Value
                  </TableCell>
                  <TableCell align='right' sx={{ fontWeight: 600 }}>
                    Avg/Claim
                  </TableCell>
                  <TableCell align='right' sx={{ fontWeight: 600 }}>
                    Status Distribution
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {routeSummaries.slice(0, 10).map((route, i) => (
                  <TableRow key={i} hover>
                    <TableCell>
                      <Typography fontWeight={500}>{route.route}</Typography>
                    </TableCell>
                    <TableCell align='right'>
                      <Chip
                        label={route.count}
                        size='small'
                        color='primary'
                        variant='outlined'
                      />
                    </TableCell>
                    <TableCell align='right'>{route.carrierCount}</TableCell>
                    <TableCell align='right'>
                      <Typography fontWeight={600}>
                        ${route.value.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align='right'>
                      $
                      {route.avgValue.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </TableCell>
                    <TableCell align='right'>
                      <Box display='flex' gap={0.5} justifyContent='flex-end'>
                        <Chip
                          label={`P:${route.paid || 0}`}
                          size='small'
                          color='success'
                          variant='outlined'
                        />
                        <Chip
                          label={`W:${route.pending || 0}`}
                          size='small'
                          color='warning'
                          variant='outlined'
                        />
                        <Chip
                          label={`R:${route.rejected || 0}`}
                          size='small'
                          color='error'
                          variant='outlined'
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default ClaimsDashboard;
